import type { EncounterSpecialty } from "@/api/encounters";
import type { CodeItem } from "@/ai-coding-outputs/admin-coding/codeComparison";
import { ROUTES } from "@/ai-coding-outputs/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { memo } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import type { DiagnosisEvidenceRow } from "../human-coding/types";
import type { UnmatchedLeadTerm } from "../human-coding/useUnmatchedLeadTerms";
import CodeReviewColumn from "./CodeReviewColumn";
import type { CodeReviewColumnProps } from "./CodeReviewColumn";
import { classificationResultQueryKey } from "./classificationResultQueryKey";
import { useCreateHumanEncounterReview } from "./useCreateHumanEncounterReview";
import { useWriteBackActions } from "./useWriteBackActions";

import { useClassificationResultWriteBack } from "./useClassificationResultWriteBack";
export interface AiResultsColumnProps {
  encounterId: string;
  inboundClassificationResultId: string | null;
  procedures: CodeItem[];
  diagnoses: CodeItem[];
  primaryDiagnosisIdentified?: boolean | null;
  notes?: string | null;
  hrgCode?: string | null;
  customerLabel: string;
  onSubmitSuccess: () => void;
  evidenceCallbacks?: CodeReviewColumnProps["evidenceCallbacks"];
  droppedCodes?: DiagnosisEvidenceRow[];
  unmatchedLeadTerms?: UnmatchedLeadTerm[];
  /** Set of code strings removed by the first human reviewer — highlights them with a red ring */
  codesRemovedByHuman?: Set<string>;
  opcsLabel?: string;
  isGoldenDatasetSupported: boolean;
  specialty?: EncounterSpecialty | null;
  patientAge?: number | null;
  patientSex?: string | null;
  historicCodes?: ReadonlySet<string>;
}

const REVIEWER_NAME = "AI";
const REVIEWER_COLOR = "#6366f1";

const AiResultsColumn = memo(
  ({
    encounterId,
    inboundClassificationResultId,
    procedures,
    diagnoses,
    primaryDiagnosisIdentified,
    notes,
    hrgCode,
    customerLabel,
    onSubmitSuccess,
    evidenceCallbacks,
    droppedCodes,
    unmatchedLeadTerms,
    codesRemovedByHuman,
    opcsLabel,
    isGoldenDatasetSupported,
    specialty,
    patientAge,
    patientSex,
    historicCodes,
  }: AiResultsColumnProps) => {
    const { writeBackClassificationResultMutation } = useWriteBackActions();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { createReview } = useCreateHumanEncounterReview();

    const { data: writeBackResult, isLoading: isLoadingWriteBackResult } = useClassificationResultWriteBack(
      inboundClassificationResultId,
      encounterId,
    );

    const isWrittenBack = writeBackResult?.isWrittenBack ?? false;
    const isSuperseded = writeBackResult?.isSuperseded ?? false;
    const currentWriteBackStatus = writeBackResult?.head?.status ?? null;
    const isCurrentWriteBackFromReview = writeBackResult?.isCurrentWriteBackFromReview ?? false;

    // Wrapper to call the mutation with the human review id
    const writeBackResultMutation = useMutation({
      mutationFn: async () => {
        await writeBackClassificationResultMutation.mutateAsync({
          inbound_classification_result_id: inboundClassificationResultId!,
        });
      },
      onSuccess: () => {
        onSubmitSuccess();
      },
      onError: (error) => {
        console.error("Failed to write back result:", error);
        toast.error(`Failed to write back result: ${error.message}`);
      },
    });

    const linkOutToReviewMutation = useMutation({
      mutationFn: async () => {
        try {
          await createReview({
            encounterId,
            procedures,
            diagnoses,
          });
          navigate(`${ROUTES.humanCodingReview.route}/${encounterId}?fromAdminReview=true`);
        } catch (error) {
          // Error handling is managed by the hook (toast notifications)
          console.error("Failed to create review:", error);
        }
      },
      onSuccess: () => {
        navigate(`${ROUTES.humanCodingReview.route}/${encounterId}?fromAdminReview=true`);
      },
      onError: (error) => {
        console.error("Failed to link out to review:", error);
        toast.error(`Failed to link out to review: ${error.message}`);
      },
    });

    if (isLoadingWriteBackResult) {
      return (
        <div
          className="space-y-3 rounded-md border border-gray-200 bg-white p-3"
          role="status"
          aria-label="Loading AI results"
        >
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="h-6 rounded bg-gray-200 animate-pulse" />
          ))}
        </div>
      );
    }

    return (
      <CodeReviewColumn
        encounterId={encounterId}
        isWrittenBack={isWrittenBack}
        isSuperseded={isSuperseded}
        currentWriteBackStatus={currentWriteBackStatus}
        isCurrentWriteBackFromReview={isCurrentWriteBackFromReview}
        reviewerName={REVIEWER_NAME}
        reviewerColor={REVIEWER_COLOR}
        procedures={procedures}
        diagnoses={diagnoses}
        primaryDiagnosisIdentified={primaryDiagnosisIdentified}
        notes={notes}
        hrgCode={hrgCode}
        customerLabel={customerLabel}
        aiCodesForComparison={undefined}
        outcome={"approved"}
        writeBackResultMutation={writeBackResultMutation}
        linkOutToReviewMutation={linkOutToReviewMutation}
        evidenceCallbacks={evidenceCallbacks}
        droppedCodes={droppedCodes}
        unmatchedLeadTerms={unmatchedLeadTerms}
        codesRemovedByHuman={codesRemovedByHuman}
        opcsLabel={opcsLabel}
        goldenDatasetSource={
          inboundClassificationResultId === null ? undefined : { kind: "ai_result", id: inboundClassificationResultId }
        }
        isGoldenDatasetSupported={isGoldenDatasetSupported}
        onGoldenDatasetCodesChanged={() =>
          queryClient.invalidateQueries({ queryKey: classificationResultQueryKey(inboundClassificationResultId) })
        }
        specialty={specialty}
        patientAge={patientAge}
        patientSex={patientSex}
        historicCodes={historicCodes}
      />
    );
  },
);

AiResultsColumn.displayName = "AiResultsColumn";

export default AiResultsColumn;
